import { useEffect, useState, useCallback, useMemo } from "react";
import { FetchBaseQueryError } from "@reduxjs/toolkit/query";
import {
  FaCheckCircle,
  FaUserPlus,
  FaPlus,
  FaTrash,
  FaSearch,
  FaBan,
  FaExclamationTriangle,
  FaTimes,
} from "react-icons/fa";
import Button from "components/button/Button";
import InputField from "components/fields/InputField";
import HeaderBox from "components/navbar/HeaderBox";
import Pagination from "components/table/Pagination";
import { Modal } from "components/modal";
import { useAppDispatch, useAppSelector } from "reduxFiles/hooks";
import { addNotification } from "reduxFiles/slices/notificationSlice";
import {
  useCreateCompanyResponderMutation,
  useCreateOrganizationRespondingMutation,
  useStatusOrganizationRespondingMutation,
  useGetOrganizationRespondingQuery,
  useLazyGetResponderByNationalCodeQuery,
} from "reduxFiles/api/assessmentApi";
import { OrganizationResponding as OrganizationRespondingType } from "types/assessmentTypes";
import { motion, AnimatePresence } from "framer-motion";
import { useTranslation } from "react-i18next";
import { useModal } from "hooks/useModal";

// Types
type FilterStatus = "ALL" | "ACTIVE" | "DEACTIVE";

interface ResponderRowState {
  id?: number;
  respondingId?: number;
  nationalCode: string;
  name: string;
  mobile: string;
  isNew: boolean;
  isToggling?: boolean;
  status?: "ACTIVE" | "DEACTIVE";
  isSubmitting?: boolean;
}

interface PaginationState {
  page: number;
  limit: number;
}

// Constants
const DEFAULT_PAGINATION: PaginationState = { page: 1, limit: 15 };

const OrganizationResponding = () => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();

  const organizationId = useAppSelector(
    (state) => state.auth?.user?.organization?.id
  );

  // ============ State ============
  const [filterStatus, setFilterStatus] = useState<FilterStatus>("ALL");
  const [rows, setRows] = useState<ResponderRowState[]>([]);
  const [newRow, setNewRow] = useState<ResponderRowState | null>(null);
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [pagination, setPagination] =
    useState<PaginationState>(DEFAULT_PAGINATION);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [pendingNationalCode, setPendingNationalCode] = useState<string>("");
  const [showNewBox, setShowNewBox] = useState<boolean>(false);
  const confirmModal = useModal();

  // ============ Queries ============
  const queryArgs = useMemo(() => {
    const term = searchTerm.trim();
    const filters: any = {};

    if (filterStatus !== "ALL") {
      filters.status = filterStatus;
    }

    if (term && term.length > 2) {
      filters.search = term;
    }

    return {
      filters,
      page: pagination.page,
      limit: pagination.limit,
    };
  }, [filterStatus, searchTerm, pagination]);

  const {
    data: respondingList,
    isFetching,
    refetch,
  } = useGetOrganizationRespondingQuery(queryArgs, {
    skip: !organizationId,
  });

  // ============ Mutations ============
  const [getResponder] = useLazyGetResponderByNationalCodeQuery();
  const [createCompanyResponder, { isLoading: isCreatingCompanyResponder }] =
    useCreateCompanyResponderMutation();
  const [addOrganizationResponding] = useCreateOrganizationRespondingMutation();
  const [statusOrganizationResponding] =
    useStatusOrganizationRespondingMutation();

  // ============ Map API Data ============
  const mapApiDataToRows = useCallback(
    (items: OrganizationRespondingType[]) => {
      return items.map((item) => ({
        id: item.responder?.id,
        respondingId: item.id,
        nationalCode: item.responder?.nationalCode || "",
        name: item.responder?.name || "",
        mobile: item.responder?.mobile || "",
        isNew: false,
        isToggling: false,
        status: item.status as "ACTIVE" | "DEACTIVE",
        isSubmitting: false,
      }));
    },
    []
  );

  // ============ Load Data ============
  useEffect(() => {
    if (!respondingList?.items?.length) {
      setRows([]);
      return;
    }

    const newRows = mapApiDataToRows(respondingList.items);
    setRows(newRows);
  }, [respondingList, mapApiDataToRows]);

  // ============ New Row Management ============
  const addNewRow = useCallback(() => {
    if (newRow && showNewBox) {
      dispatch(
        addNotification({
          type: "warning",
          message: t("responding.alreadyHasNewRow"),
        })
      );
      return;
    }

    const newRowData: ResponderRowState = {
      id: undefined,
      respondingId: undefined,
      nationalCode: "",
      name: "",
      mobile: "",
      isNew: true,
      isToggling: false,
      isSubmitting: false,
    };

    setNewRow(newRowData);
    setShowNewBox(true);
  }, [newRow, showNewBox, dispatch, t]);

  const removeNewRow = useCallback(() => {
    setNewRow(null);
    setShowNewBox(false);
  }, []);

  const updateNewRow = useCallback((data: Partial<ResponderRowState>) => {
    setNewRow((prev) =>
      prev
        ? {
            ...prev,
            ...data,
            ...(data.nationalCode !== undefined && {
              id: undefined,
              name: "",
              mobile: "",
            }),
          }
        : null
    );
  }, []);

  // ============ Register Responder ============
  const handleRegisterResponder = useCallback(async () => {
    if (!newRow?.nationalCode) {
      dispatch(
        addNotification({
          type: "warning",
          message: t("responding.enterNationalCode"),
        })
      );
      return;
    }

    // Validate national code format
    if (!/^\d{10}$/.test(newRow.nationalCode)) {
      dispatch(
        addNotification({
          type: "error",
          message: t("responding.invalidNationalCode"),
        })
      );
      return;
    }

    setIsSubmitting(true);

    try {
      // First check if responder exists
      let responderData;
      try {
        responderData = await getResponder(newRow.nationalCode).unwrap();
      } catch (err) {
        const error = err as FetchBaseQueryError;
        if (
          "data" in error &&
          error.data &&
          typeof error.data === "object" &&
          "statusCode" in error.data &&
          error.data.statusCode === 404
        ) {
          // Responder not found, open modal
          setPendingNationalCode(newRow.nationalCode);
          confirmModal.openModal();
          return;
        } else {
          throw err;
        }
      }

      // If responder found, register
      if (responderData?.responder) {
        await addOrganizationResponding({
          organizationId: Number(organizationId),
          items: [responderData.responder.id],
        }).unwrap();

        setNewRow(null);
        setShowNewBox(false);

        await refetch();
      }
    } catch (err) {
      console.error(err);
    } finally {
      setIsSubmitting(false);
    }
  }, [
    newRow,
    getResponder,
    addOrganizationResponding,
    organizationId,
    refetch,
    dispatch,
    t,
    confirmModal,
  ]);

  // ============ Create and Register New Responder ============
  const handleCreateAndRegister = useCallback(async () => {
    if (!pendingNationalCode) return;

    confirmModal.closeModal();
    setIsSubmitting(true);

    try {
      // Create new responder
      const responderData = await createCompanyResponder({
        nationalCode: pendingNationalCode,
      }).unwrap();

      if (responderData?.responder) {
        // Register the new responder
        await addOrganizationResponding({
          organizationId: Number(organizationId),
          items: [responderData.responder.id],
        }).unwrap();

        setNewRow(null);
        setShowNewBox(false);
        await refetch();
      }
    } catch (err) {
      console.error(err);
    } finally {
      setIsSubmitting(false);
      setPendingNationalCode("");
    }
  }, [
    pendingNationalCode,
    confirmModal,
    createCompanyResponder,
    addOrganizationResponding,
    organizationId,
    refetch,
  ]);

  // ============ Toggle Status ============
  const handleToggleStatus = useCallback(
    async (row: ResponderRowState, index: number) => {
      if (!row.respondingId) return;

      setRows((prev) =>
        prev.map((r, i) => (i === index ? { ...r, isToggling: true } : r))
      );

      try {
        await statusOrganizationResponding(row.respondingId).unwrap();
        await refetch();

        dispatch(
          addNotification({
            type: "success",
            message:
              row.status === "ACTIVE"
                ? t("responding.deactivatedSuccess")
                : t("responding.activatedSuccess"),
          })
        );
      } catch (err) {
        console.error(err);
      } finally {
        setRows((prev) =>
          prev.map((r, i) => (i === index ? { ...r, isToggling: false } : r))
        );
      }
    },
    [statusOrganizationResponding, refetch, dispatch, t]
  );

  // ============ Pagination ============
  const handlePageChange = useCallback((page: number) => {
    setPagination((prev) => ({ ...prev, page }));
  }, []);

  // ============ Search ============
  const handleSearchChange = useCallback((value: string) => {
    setSearchTerm(value);
    if (value.length > 2 || value.length === 0) {
      setPagination((prev) => ({ ...prev, page: 1 }));
    }
  }, []);

  // ============ Computed Values ============
  const totalPages = respondingList?.pageCount || 1;
  const currentPage = pagination.page;
  const showPagination = totalPages > 1;

  // Filter rows for display
  const displayRows = useMemo(() => {
    if (filterStatus === "ALL") return rows;
    return rows.filter((row) => row.status === filterStatus);
  }, [rows, filterStatus]);

  const isEmpty = !isFetching && displayRows.length === 0;

  // ============ Filter Options ============
  const filterOptions = [
    { value: "ALL", label: t("actions.all") },
    { value: "ACTIVE", label: t("responding.active") },
    { value: "DEACTIVE", label: t("responding.deactive") },
  ];

  // ============ Render ============
  return (
    <div className="mt-5">
      <HeaderBox
        title={t("responding.title")}
        createBtn={{
          title: t("responding.addResponder"),
          icon: <FaPlus size={16} />,
          handler: addNewRow,
        }}
        refreshBtn={!isFetching ? () => refetch() : undefined}
      />

      {/* Filters & Search */}
      <div className="mt-4 flex min-h-[60px] flex-wrap items-center gap-4">
        {/* Filter Select */}
        <div className="flex items-center gap-2">
          <div className="relative">
            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value as FilterStatus)}
              className="appearance-none rounded-2xl border-0 bg-gray-100 py-2.5 pl-10 pr-10 text-sm text-gray-700 transition-all duration-200 hover:bg-gray-200 focus:bg-white focus:ring-2 focus:ring-brand-500/30 dark:bg-white/5 dark:text-white dark:focus:bg-navy-800"
            >
              {filterOptions.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
            <svg
              className="pointer-events-none absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400 dark:text-gray-500"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z"
              />
            </svg>
            <div className="pointer-events-none absolute right-3.5 top-1/2 -translate-y-1/2 text-gray-400 dark:text-gray-500">
              <svg
                className="h-4 w-4"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M19 9l-7 7-7-7"
                />
              </svg>
            </div>
          </div>
        </div>

        {/* Search Input */}
        <div className="min-w-[200px] flex-1 sm:max-w-[350px]">
          <div className="relative">
            <FaSearch className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400 transition-colors dark:text-gray-500" />
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => handleSearchChange(e.target.value)}
              placeholder={t("responding.search")}
              className="w-full rounded-2xl border-0 bg-gray-100 py-2.5 pl-11 pr-4 text-sm text-gray-700 transition-all duration-200 placeholder:text-gray-400 focus:bg-white focus:ring-2 focus:ring-brand-500/30 dark:bg-white/5 dark:text-white dark:placeholder:text-gray-500 dark:focus:bg-navy-800"
            />
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="mt-4 transition-all duration-300">
        {/* New Items Box */}
        <AnimatePresence>
          {showNewBox && newRow && (
            <motion.div
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              className="mb-6 w-full rounded-2xl border-2 border-dashed border-brand-300 bg-brand-50/30 p-4 dark:border-brand-700 dark:bg-brand-900/10 md:w-1/2 lg:w-1/3"
            >
              <div className="mb-4 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="flex h-8 w-8 items-center justify-center rounded-full bg-brand-100 text-brand-600 dark:bg-brand-900/30 dark:text-brand-400">
                    <FaUserPlus size={14} />
                  </div>
                  <h3 className="text-sm font-semibold text-brand-700 dark:text-brand-400">
                    {t("responding.addNew")}
                  </h3>
                </div>
                <button
                  onClick={removeNewRow}
                  className="rounded-lg p-1.5 text-gray-400 transition-all hover:bg-gray-100 hover:text-gray-600 dark:hover:bg-gray-800 dark:hover:text-gray-300"
                  title={t("buttons.close")}
                >
                  <FaTimes size={16} />
                </button>
              </div>

              <div className="grid grid-cols-1">
                <motion.div
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.95 }}
                  className="rounded-2xl border border-brand-200 bg-white p-5 shadow-sm dark:border-brand-800 dark:bg-navy-800"
                >
                  {/* Header */}
                  <div className="mb-4 flex items-center justify-between">
                    <h5 className="font-bold text-gray-800 dark:text-white">
                      {t("responder.responder")} {t("responding.addNew")}
                    </h5>
                    <span className="rounded-full bg-yellow-100 px-3 py-1 text-xs font-medium text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400">
                      {t("responding.need")}
                    </span>
                  </div>

                  {/* National Code Input */}
                  <div className="space-y-3">
                    <div>
                      <InputField
                        label={t("responder.nationalCode")}
                        id="nationalCode-new"
                        value={newRow.nationalCode || ""}
                        onChange={(e) =>
                          updateNewRow({
                            nationalCode: e.target.value
                              .replace(/\D/g, "")
                              .slice(0, 10),
                          })
                        }
                        helper={
                          <span className="mt-1 flex items-center gap-1 text-xs text-gray-500 dark:text-gray-400">
                            {t("responding.enterNationalCodeHint")}
                          </span>
                        }
                      />
                    </div>

                    {/* Action Buttons */}
                    <div className="flex items-center gap-2">
                      <Button
                        className="flex-1"
                        size="sm"
                        variant="info"
                        loading={isSubmitting}
                        disabled={
                          !newRow.nationalCode ||
                          newRow.nationalCode.length < 10
                        }
                        onClick={handleRegisterResponder}
                        startIcon={<FaUserPlus size={14} />}
                      >
                        {t("responding.register")}
                      </Button>

                      <Button
                        variant="outline"
                        size="sm"
                        className="border-red-300 text-red-600 hover:bg-red-50 dark:border-red-700 dark:text-red-400 dark:hover:bg-red-900/20"
                        onClick={removeNewRow}
                        startIcon={<FaTrash size={14} />}
                      >
                        {t("buttons.delete")}
                      </Button>
                    </div>
                  </div>
                </motion.div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Loading */}
        {isFetching && displayRows.length === 0 && (
          <div className="py-10 text-center">
            <div className="loader mx-auto w-6 text-brand-500" />
          </div>
        )}

        {/* Empty State */}
        {isEmpty && (
          <div className="rounded-xl border border-dashed border-gray-300 p-8 text-center text-gray-700 transition-all dark:border-gray-700 dark:text-gray-300">
            <p className="text-lg">
              {filterStatus === "ALL" && t("responding.emptyMsg")}
              {filterStatus === "ACTIVE" && t("responding.emptyActive")}
              {filterStatus === "DEACTIVE" && t("responding.emptyDeactive")}
            </p>
          </div>
        )}

        {/* Main List */}
        {!isEmpty && (
          <>
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
              <AnimatePresence>
                {displayRows.map((row, index) => {
                  const actualIndex = rows.findIndex(
                    (r) => r.respondingId === row.respondingId
                  );
                  const isActive = row.status === "ACTIVE";

                  return (
                    <motion.div
                      key={row.respondingId || `row-${index}`}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, scale: 0.95 }}
                      transition={{ duration: 0.3 }}
                      className={`
                        group relative
                        rounded-2xl
                        border
                        bg-white
                        p-5
                        shadow-sm
                        transition-all
                        hover:shadow-lg
                        dark:bg-navy-800
                        ${
                          isActive
                            ? "border-green-200 dark:border-green-800"
                            : "border-red-200 dark:border-red-800"
                        }
                      `}
                    >
                      {/* Status Indicator Dot */}
                      <div
                        className={`
                          absolute right-4 top-4 h-2 w-2 rounded-full
                          ${isActive ? "bg-green-500" : "bg-red-500"}
                        `}
                      />

                      {/* Header */}
                      <div className="mb-4 flex flex-wrap items-center justify-between gap-2">
                        <h5 className="font-bold text-gray-800 dark:text-white">
                          {row.name || t("responding.noname")}
                        </h5>

                        <div className="flex items-center gap-2">
                          {/* Status Badge */}
                          <span
                            className={`inline-flex items-center rounded-full p-2 text-xs font-medium ${
                              isActive
                                ? "bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400"
                                : "bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400"
                            }`}
                          >
                            {isActive ? (
                              <FaCheckCircle className="h-3 w-3" />
                            ) : (
                              <FaBan className="h-3 w-3" />
                            )}
                          </span>

                          {/* Toggle Button with Text */}
                          <button
                            onClick={() => handleToggleStatus(row, actualIndex)}
                            disabled={row.isToggling}
                            className={`
                              rounded-lg px-3 py-1.5 text-xs font-medium transition-all duration-200
                              ${
                                isActive
                                  ? "bg-red-100 text-red-600 hover:bg-red-200 dark:bg-red-900/30 dark:text-red-400 dark:hover:bg-red-900/50"
                                  : "bg-green-100 text-green-600 hover:bg-green-200 dark:bg-green-900/30 dark:text-green-400 dark:hover:bg-green-900/50"
                              }
                              ${
                                row.isToggling
                                  ? "cursor-not-allowed opacity-50"
                                  : "hover:scale-105"
                              }
                            `}
                            title={
                              isActive
                                ? t("buttons.deactivate")
                                : t("buttons.activate")
                            }
                          >
                            {row.isToggling ? (
                              <div className="border-current border-t-transparent animate-spin h-3 w-3 rounded-full border-2" />
                            ) : isActive ? (
                              t("buttons.deactivate")
                            ) : (
                              t("buttons.activate")
                            )}
                          </button>
                        </div>
                      </div>

                      {/* Info */}
                      <div className="space-y-1 text-sm text-gray-600 dark:text-gray-400">
                        <div>
                          <span className="font-medium">
                            {t("responder.nationalCode")}:
                          </span>{" "}
                          {row.nationalCode}
                        </div>
                        <div>
                          <span className="font-medium">
                            {t("responding.mobile")}:
                          </span>{" "}
                          {row.mobile || "-"}
                        </div>
                      </div>
                    </motion.div>
                  );
                })}
              </AnimatePresence>
            </div>

            {/* Pagination */}
            {showPagination && (
              <div className="mt-6 flex justify-center">
                <Pagination
                  currentPage={currentPage}
                  totalPages={totalPages}
                  onPageChange={handlePageChange}
                />
              </div>
            )}
          </>
        )}
      </div>

      {/* Confirm Modal for Creating New Responder */}
      <Modal isOpen={confirmModal.isOpen} onClose={confirmModal.closeModal}>
        <div className="p-6 text-center">
          <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-yellow-100">
            <FaExclamationTriangle className="h-10 w-10 text-yellow-500" />
          </div>

          <h3 className="mb-2 text-xl font-semibold text-gray-900 dark:text-white">
            {t("responding.confirmCreateTitle")}
          </h3>

          <p className="mb-2 text-gray-700 dark:text-gray-400">
            {t("responding.confirmCreateDesc")}
          </p>

          <p className="mb-6 text-lg font-medium text-brand-600 dark:text-brand-400">
            {t("responding.nationalCodeLabel")}: {pendingNationalCode}
          </p>

          <div className="flex flex-col gap-3 sm:flex-row sm:justify-center">
            <Button
              variant="outline"
              className="flex-1"
              onClick={() => {
                confirmModal.closeModal();
                setPendingNationalCode("");
              }}
            >
              {t("buttons.cancel")}
            </Button>
            <Button
              variant="info"
              className="flex-1"
              onClick={handleCreateAndRegister}
              startIcon={<FaUserPlus size={18} />}
              loading={isCreatingCompanyResponder}
            >
              {t("responding.confirmCreate")}
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default OrganizationResponding;
